// @flow
import React, { memo } from 'react';
import { findDOMNode } from 'react-dom';
import Toolbar from './Toolbar';
import DrawingCanvas from '../tabs/DrawingCanvas';
import ThumbnailUtil from '../util/ThumbnailUtil';
import CommentUtil from '../util/CommentUtil';
import CollectionAssetView from '../tabs/collections/CollectionAssetView';
import Store from '../store/Store';
import {
  SYNC_VIDEOS,
  VIDEO_CONTROLLER,
  MULTIPAGE_ASSET_CURRENT_PAGE,
  ASSET_SCALE_INFO,
  COMMENT_FILTER_BY,
  ASSET_PREVIEW_INFO
} from '../store/Constants';
import VideoController from '../video/VideoController';
import { RepoItem, ZmCollection } from '../autogenerated/ZoomBridge';
import StoreListener from '../store/StoreListener';
import { ScaleableAssetViewWithComments } from '../assets/ScaleableAssetView';
import { CommentFilterOption } from '../util/Constants';
import Spinner from '../common/Spinner';
import Center from '../common/layout/Center';
import PreviewHelper from '../helper/PreviewHelper';

import './CompareRevisions.css';
import ViewUtil from '../ViewUtil';
import AssetVideoPreview from '../tabs/AssetVideoPreview';
import AssetImagePreview from '../tabs/AssetImagePreview';
import { getHeaderHeight, getInnerHeight } from '../util/ScreenUtil';
import Portal from '../common/Portal';
import FileInfoUtil from '../util/FileInfoUtil';

type Props = {
  left: RepoItem,
  right: RepoItem,
  collection: ZmCollection,
  isVideo: boolean,
  isMultiPage: boolean,
  onExitClick: Function,
  SYNC_VIDEOS: boolean,
  ASSET_SCALE_INFO: Object,
  VIDEO_CONTROLLER: VideoController,
  ASSET_PREVIEW_INFO: PreviewHelper,
  MULTIPAGE_ASSET_CURRENT_PAGE: number
};

type State = {
  overlay: ?Object,
  overlayToggled: boolean,
  loading: boolean
};

const storeItems = [SYNC_VIDEOS, ASSET_SCALE_INFO, VIDEO_CONTROLLER, ASSET_PREVIEW_INFO, MULTIPAGE_ASSET_CURRENT_PAGE];

@StoreListener(storeItems)
export default class CompareRevisions extends React.Component<Props, State> {
  _ignoreScroll1: boolean = false;
  _ignoreScroll2: boolean = false;
  _ignoreScroll1Timer: number;
  _ignoreScroll2Timer: number;

  state = {
    overlay: null,
    overlayToggled: false,
    loading: false,
    isLeftImageAvailable: false,
    isRightImageAvailable: false
  };

  componentDidMount() {
    const { isVideo, is3DAsset, SYNC_VIDEOS: sync, VIDEO_CONTROLLER: controller } = this.props;
    if (isVideo) {
      if (sync) {
        setTimeout(() => {
          //controller.playing = false;
          this.playerSync();
        }, 500);
      }
    } else if (is3DAsset) {
      return;
    } else {
      this.setOverlay();
    }

    this.addListeners();
    Store.put(COMMENT_FILTER_BY, CommentFilterOption.ALL);
  }

  //One time seek when Video player start
  playerSync = () => {
    const { VIDEO_CONTROLLER: controller } = this.props;
    const video: HTMLVideoElement = findDOMNode(this).querySelector('video');
    controller.setCurrentTime(video.currentTime, true);
    controller.playing = true;
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!prevState.overlay && this.state.overlay) {
      this.animate();
    }

    if (this.props.isMultiPage && this.props.MULTIPAGE_ASSET_CURRENT_PAGE !== prevProps.MULTIPAGE_ASSET_CURRENT_PAGE) {
      this.setOverlay();
    }
  }

  componentWillUnmount() {
    if (!this.props[SYNC_VIDEOS]) {
      Store.put(SYNC_VIDEOS, true);
    }

    clearInterval(this._timer);
    this.removeListeners();
  }

  addListeners = () => {
    const nodes = this.getScrollNodes();
    nodes.forEach((n) => n.addEventListener('scroll', this.onScroll));
  };

  removeListeners = () => {
    const nodes = this.getScrollNodes();
    nodes.forEach((n) => n.removeEventListener('scroll', this.onScroll));
  };

  getScrollNodes = () => {
    return [...document.querySelectorAll('[name="ScaleableAssetView"]')];
  };

  onScroll = (e) => {
    const [n1, n2] = this.getScrollNodes();
    if (e.target === n1 && !this._ignoreScroll1) {
      this._ignoreScroll2 = true;
      clearTimeout(this._ignoreScroll2Timer);
      this._ignoreScroll2Timer = setTimeout(() => (this._ignoreScroll2 = false), 500);
      n2.scrollTop = n1.scrollTop;
      n2.scrollLeft = n1.scrollLeft;
    }

    if (e.target === n2 && !this._ignoreScroll2) {
      this._ignoreScroll1 = true;
      clearTimeout(this._ignoreScroll1Timer);
      this._ignoreScroll1Timer = setTimeout(() => (this._ignoreScroll1 = false), 500);
      n1.scrollTop = n2.scrollTop;
      n1.scrollLeft = n2.scrollLeft;
    }
  };

  getCanvas() {
    if (!this.canvas) {
      this.canvas = this.refs.assetPreviewR.refs.assetImagePreviewR.refs.drawingCanvas;
    }
    return this.canvas;
  }

  setOverlay = () => {
    const { isMultiPage, MULTIPAGE_ASSET_CURRENT_PAGE: pageNumber } = this.props;
    const src1 = ThumbnailUtil.createImagePreviewUrl(this.props.left, isMultiPage ? pageNumber : '');
    const src2 = ThumbnailUtil.createImagePreviewUrl(this.props.right, isMultiPage ? pageNumber : '');
    this.setState({ overlay: { src1, src2 } });
  };

  animate = () => {
    this._timer = setInterval(() => {
      this.setState({ overlayToggled: !this.state.overlayToggled });
    }, 2000);
  };

  placeText = () => {
    this.getCanvas().toggleTextInput();
  };

  placeRect = () => {
    this.getCanvas().toggleShapeInput(DrawingCanvas.DRAWING_MODE_RECT);
  };

  placeEllipse = () => {
    this.getCanvas().toggleShapeInput(DrawingCanvas.DRAWING_MODE_ELLIPSE);
  };

  placeLine = () => {
    this.getCanvas().toggleShapeInput(DrawingCanvas.DRAWING_MODE_LINE);
  };

  onSyncVideoClick = (e) => {
    const syncVideos = e.target.checked;
    Store.put(SYNC_VIDEOS, syncVideos);

    if (syncVideos) {
      const controller: VideoController = Store.get(VIDEO_CONTROLLER);
      const video: HTMLVideoElement = findDOMNode(this).querySelector('video');
      controller.volume = video.volume;
      controller.muted = video.muted;
      controller.playbackRate = video.playbackRate;
      controller.setCurrentTime(video.currentTime, true);
      controller.playing = true;
    }
  };

  onCopyCommentsLtoR = () => {
    const { left, right } = this.props;
    this.copyComments(left, right);
  };

  onCopyCommentsRtoL = () => {
    const { left, right } = this.props;
    this.copyComments(right, left);
  };

  stopLoading = () => {
    setTimeout(() => this.setState({ loading: false }), 1000);
  };

  copyComments = (fromAsset: RepoItem, toAsset: RepoItem) => {
    this.setState({ loading: true });
    const collectionId = this.props.collection.getCollectionId();
    CommentUtil.copyComments(fromAsset, toAsset, collectionId)
      .then(this.stopLoading)
      .catch((err) => {
        console.error(err);
        this.stopLoading();
      });
  };

  onScaleChange = (scale: number, firstRun: ?boolean) => {
    const scaleInfo = { ...this.props[ASSET_SCALE_INFO], scale };
    if (firstRun) {
      scaleInfo.baseline = scale;
      scaleInfo.range = {
        min: scale / 2,
        max: scale * 20
      };
    }

    Store.put(ASSET_SCALE_INFO, scaleInfo);
  };

  onPageChange = (pageNumber: number) => {
    Store.put(MULTIPAGE_ASSET_CURRENT_PAGE, pageNumber);
  };

  isComaparedImgAvailable = (isImgAvailable: boolean, isLeftImg: boolean) => {
    isLeftImg
      ? this.setState({ isLeftImageAvailable: isImgAvailable })
      : this.setState({ isRightImageAvailable: isImgAvailable });
  };

  renderRevision(revision: RepoItem, index: number) {
    const { collection, isVideo, is3DAsset } = this.props;
    const currentPage = this.props[MULTIPAGE_ASSET_CURRENT_PAGE];
    const isLeftImg = index ? false : true;
    let blockVideoKeyEvents = false;
    if (
      (this.refs.assetPreviewL && this.refs.assetPreviewL._component.isVideoKeyEventsBlocked()) ||
      (this.refs.assetPreviewR && this.refs.assetPreviewR._component.isVideoKeyEventsBlocked())
    ) {
      blockVideoKeyEvents = true;
    }
    const isAssetsTabActive = ViewUtil.isAssetTabActive();
    if (isAssetsTabActive) {
      const assettabProps = {
        selectedAsset: revision,
        isCompareMode: true,
        waveplayer: is3DAsset ? true : false
      };
      if (isVideo || is3DAsset) {
        return <AssetVideoPreview {...assettabProps} />;
      } else {
        const aipProps = {
          ref: 'assetImagePreview',
          selectedAsset: revision,
          isAIData: false,
          isLeftImg: isLeftImg,
          isComaparedImgAvailable: this.isComaparedImgAvailable,
          isMultiPage: false, //FileInfoUtil.isMultiPage(revision),
          isCompareMode: true
        };
        return <AssetImagePreview {...aipProps} />;
      }
    }

    if (isVideo || is3DAsset) {
      let props = {
        asset: revision,
        collection: collection,
        selectedAsset: revision,
        allowFullscreen: false,
        blockVideoKeyEvents: blockVideoKeyEvents,
        compareViewRevisionIndex: index,
        useLocalComments: true,
        waveplayer: is3DAsset ? true : false,
        isCompareMode: true
      };

      if (index === 0) {
        props.ref = 'assetPreviewL';
      } else {
        props.ref = 'assetPreviewR';
      }

      return <CollectionAssetView {...props} />;
    }

    return (
      <ScaleableAssetViewWithComments
        asset={revision}
        isLeftImg= {isLeftImg}
        isComaparedImgAvailable= {this.isComaparedImgAvailable}
        collection={collection}
        src={ThumbnailUtil.createImagePreviewUrl(revision, currentPage)}
        onScaleChange={this.onScaleChange}
        useLocalComments
      />
    );
  }

  render() {
    const {
      left,
      right,
      isVideo,
      isMultiPage,
      onExitClick,
      ASSET_PREVIEW_INFO: previewInfo,
      MULTIPAGE_ASSET_CURRENT_PAGE: pageNumber
    } = this.props;
    const syncVideos = this.props[SYNC_VIDEOS];
    const { scale, baseline, range } = this.props[ASSET_SCALE_INFO];
    const { overlay, overlayToggled, loading, isLeftImageAvailable, isRightImageAvailable } = this.state;
    let multipageProps;
    if (isMultiPage) {
      multipageProps = {
        isMultiPage: true,
        current: pageNumber,
        total: previewInfo.totalPages,
        onChange: this.onPageChange
      };
    } else {
      multipageProps = {
        isMultiPage: false
      };
    }
    const noPreviewAvailableImgSrc = 'images/no_preview_available.png';
    return (
      <div className="flex-col" name="CompareRevisions" style={{ height: getInnerHeight() - getHeaderHeight() }}>
        {false ? (
          <div className="flex-main flex-row">
            <Center>
              <Spinner large />
            </Center>
          </div>
        ) : (
          <div className={`flex-main flex-row ${isVideo ? 'align-center' : ''} ${syncVideos ? 'sync-videos' : ''}`}>
            <div id="compareRevision0" className="flex-main wrapper" style={{ marginRight: '2px' }}>
              {this.renderRevision(left, 0)}
            </div>
            {overlay ? (
              <div style={{ marginRight: '2px' }} className={`flex-main overlay ${overlayToggled ? 'toggled' : ''}`}>
                {'pdf' == this.props.fileType ? (
                  <>
                    <div className="pdf-compare">{this.renderRevision(left, 0)}</div>
                    <div className="pdf-compare"> {this.renderRevision(right, 1)}</div>
                  </>
                ) : (
                  <>
                    <img src={isLeftImageAvailable ? overlay.src1 : noPreviewAvailableImgSrc} />
                    <img src={isRightImageAvailable ? overlay.src2 : noPreviewAvailableImgSrc} />
                  </>
                )}
              </div>
            ) : null}
            <div id="compareRevision1" className="flex-main wrapper">
              {this.renderRevision(right, 1)}
            </div>
          </div>
        )}
        <Toolbar
          names={[left.getName(), right.getName()]}
          revNums={[left.getFrn(), right.getFrn()]}
          isVideo={isVideo}
          syncVideos={syncVideos}
          onTextClick={this.placeText}
          onRectClick={this.placeRect}
          onCircleClick={this.placeEllipse}
          onLineClick={this.placeLine}
          onSyncVideoClick={this.onSyncVideoClick}
          onExitClick={onExitClick}
          onCopyCommentsLtoRClick={this.onCopyCommentsLtoR}
          onCopyCommentsRtoLClick={this.onCopyCommentsRtoL}
          disableCopyLtoR={this.state.disableCopyLtoR}
          disableCopyRtoL={this.state.disableCopyRtoL}
          scale={scale}
          baseline={baseline}
          range={range}
          onScaleChange={this.onScaleChange}
          multipageProps={multipageProps}
          is3DAsset={this.props.is3DAsset}
        />
      </div>
    );
  }
}

export const CompareView = ({ assets, onClose, fileType }) => {
  if (assets) {
    const props = {
      left: assets[0],
      right: assets[1],
      isVideo: FileInfoUtil.isAudioOrVideo(assets[0]),
      isMultiPage: FileInfoUtil.isMultiPage(assets[0]),
      onExitClick: onClose,
      is3DAsset: FileInfoUtil.is3DAsset(assets[0]),
      fileType: fileType
    };
    return (
      <Portal defaultOpen={true}>
        <div
          name="CompareRevisions_Repo"
          style={{ position: 'fixed', height: '100%', width: '100%', top: '0px', left: '0px', zIndex: 100 }}>
          <CompareRevisions {...props} />
        </div>
      </Portal>
    );
  }
  return null;
};
